/**
 * html2canvas 1.4.x cannot parse CSS Color 4 values such as `color(srgb …)`,
 * `oklch(…)`, or `color-mix(…)`. Chromium often resolves `color-mix` / oklab
 * utilities to `color(srgb …)` in computed style, which aborts full-page export.
 *
 * Rewrite paint-related computed styles on the **clone only** to rgb/hex that
 * html2canvas can parse. Never mutate the live document.
 *
 * Inlined values use `setProperty(..., 'important')` so they win over stylesheet
 * `!important` color-mix utilities (e.g. Tailwind `!bg-[color-mix(...)]`).
 */

import { FullPageExportStageError } from './types.js';

const UNSUPPORTED_COLOR_FN = /\b(?:color-mix|oklch|oklab|lab|lch|color)\(/i;

/**
 * Direct color properties html2canvas `CSSParsedDeclaration` color-parses
 * (plus a few defensive SVG/UI channels). `webkitTextStrokeColor` is required
 * (html2canvas parses it). `filter` / `webkitTextFillColor` / `mask*` are NOT
 * parsed by html2canvas 1.4.1 and have zero in-repo modern-color usage — omitted
 * from DIRECT; `filter` is on COMPOSITE for defensive rewrite if ever inlined.
 */
const DIRECT_COLOR_STYLE_PROPS = [
  'color',
  'backgroundColor',
  'borderTopColor',
  'borderRightColor',
  'borderBottomColor',
  'borderLeftColor',
  'outlineColor',
  'textDecorationColor',
  'caretColor',
  'columnRuleColor',
  'fill',
  'stroke',
  'stopColor',
  'floodColor',
  'lightingColor',
  'webkitTextStrokeColor',
] as const;

/**
 * Values that may embed modern color functions (gradients, shadows, images).
 * `listStyleImage` is parsed by html2canvas via `image.parse` → color stops.
 * `filter` is not parsed by html2canvas 1.4.1 but is rewritten defensively.
 * `background` shorthand covers copyCSSStyles leftover from pseudo materialization.
 */
const COMPOSITE_COLOR_STYLE_PROPS = [
  'backgroundImage',
  'boxShadow',
  'textShadow',
  'borderImageSource',
  'listStyleImage',
  'filter',
] as const;

type DirectColorStyleProp = (typeof DIRECT_COLOR_STYLE_PROPS)[number];
type CompositeColorStyleProp = (typeof COMPOSITE_COLOR_STYLE_PROPS)[number];

const HTML2CANVAS_PSEUDO_SELECTOR = 'html2canvaspseudoelement';

function cssValueNeedsSanitize(value: string): boolean {
  return value !== '' && value !== 'none' && UNSUPPORTED_COLOR_FN.test(value);
}

/**
 * Extract one CSS function call starting at `openIndex` (index of '('),
 * with balanced parentheses. Returns end index exclusive, or -1.
 */
function findBalancedCallEnd(value: string, openIndex: number): number {
  let depth = 0;
  for (let i = openIndex; i < value.length; i += 1) {
    const ch = value[i];
    if (ch === '(') {
      depth += 1;
    } else if (ch === ')') {
      depth -= 1;
      if (depth === 0) {
        return i + 1;
      }
    }
  }
  return -1;
}

/**
 * Replace every unsupported color function token in `value` via `convertColor`.
 * Unknown / unconvertible tokens fall back to `transparent` so capture can proceed.
 */
export function rewriteUnsupportedCssColors(
  value: string,
  convertColor: (cssColor: string) => string | null,
): string {
  if (!cssValueNeedsSanitize(value)) {
    return value;
  }

  let out = '';
  let i = 0;
  while (i < value.length) {
    const slice = value.slice(i);
    const match = /(?:color-mix|oklch|oklab|lab|lch|color)\(/i.exec(slice);
    if (match == null || match.index == null) {
      out += slice;
      break;
    }
    const start = i + match.index;
    out += value.slice(i, start);
    const openParen = start + match[0].length - 1;
    const end = findBalancedCallEnd(value, openParen);
    if (end < 0) {
      out += value.slice(start);
      break;
    }
    const token = value.slice(start, end);
    out += convertColor(token) ?? 'transparent';
    i = end;
  }
  return out;
}

/** Browser canvas normalizes modern CSS colors to rgb/hex that html2canvas accepts. */
export function createCssColorToRgbConverter(
  doc: Document = document,
): (cssColor: string) => string | null {
  const canvas = doc.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  let ctx: CanvasRenderingContext2D | null = null;
  try {
    ctx = canvas.getContext('2d');
  } catch {
    ctx = null;
  }
  if (ctx == null) {
    return () => null;
  }
  return (cssColor: string): string | null => {
    if (!cssValueNeedsSanitize(cssColor)) {
      return cssColor;
    }
    try {
      ctx.fillStyle = '#000000';
      ctx.fillStyle = cssColor;
      const normalized = ctx.fillStyle;
      if (typeof normalized !== 'string' || normalized === '' || cssValueNeedsSanitize(normalized)) {
        return null;
      }
      return normalized;
    } catch {
      return null;
    }
  };
}

function toKebabCssProp(prop: string): string {
  const kebab = prop.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
  // camelCase vendor props (webkitTextStrokeColor) must map to -webkit-…, not webkit-…
  if (
    kebab.startsWith('webkit-') ||
    kebab.startsWith('moz-') ||
    kebab.startsWith('ms-')
  ) {
    return `-${kebab}`;
  }
  return kebab;
}

function fallbackDirectColor(prop: DirectColorStyleProp): string {
  if (prop === 'backgroundColor') {
    return '#ffffff';
  }
  if (prop === 'color' || prop === 'fill' || prop === 'caretColor') {
    return '#111111';
  }
  return 'transparent';
}

/** Cross-realm safe: clone iframe nodes fail parent-realm `instanceof HTMLElement`. */
function isPaintElement(node: Node | null | undefined): node is Element & { style: CSSStyleDeclaration } {
  if (node == null || node.nodeType !== 1) {
    return false;
  }
  const el = node as Element & { style?: CSSStyleDeclaration };
  return el.style != null && typeof el.style.setProperty === 'function';
}

function readInlineFromStyleAttribute(styleAttr: string, kebab: string): string {
  if (styleAttr === '') {
    return '';
  }
  const escaped = kebab.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = new RegExp(`(?:^|;)\\s*${escaped}\\s*:\\s*([^;]+)`, 'i').exec(styleAttr);
  return match?.[1]?.trim() ?? '';
}

function readStyleColorCandidate(
  el: Element,
  computed: CSSStyleDeclaration,
  style: CSSStyleDeclaration,
  prop: DirectColorStyleProp | CompositeColorStyleProp,
): string {
  const kebab = toKebabCssProp(prop);
  const styleRecord = style as unknown as Record<string, string>;
  const computedRecord = computed as unknown as Record<string, string>;
  const fromAttr = readInlineFromStyleAttribute(
    el.getAttribute('style') ?? style.cssText ?? '',
    kebab,
  );
  const inline = style.getPropertyValue(kebab) || styleRecord[prop] || fromAttr || '';
  const fromComputed = computed.getPropertyValue(kebab) || computedRecord[prop] || '';
  // Prefer any candidate that still carries an unsupported color function.
  if (cssValueNeedsSanitize(inline)) {
    return inline;
  }
  if (cssValueNeedsSanitize(fromComputed)) {
    return fromComputed;
  }
  if (cssValueNeedsSanitize(fromAttr)) {
    return fromAttr;
  }
  return inline || fromComputed || fromAttr;
}

function writeSanitizedStyle(
  style: CSSStyleDeclaration,
  prop: DirectColorStyleProp | CompositeColorStyleProp,
  value: string,
): void {
  const kebab = toKebabCssProp(prop);
  style.setProperty(kebab, value, 'important');
  // jsdom / some engines keep a stale camelCase mirror for -webkit-* that getPropertyValue misses.
  const styleRecord = style as unknown as Record<string, string>;
  if (Object.prototype.hasOwnProperty.call(styleRecord, prop) || prop.startsWith('webkit')) {
    try {
      styleRecord[prop] = value;
    } catch {
      // Some CSSStyleDeclaration mirrors are read-only — setProperty is enough in real browsers.
    }
  }
}

function sanitizeElementPaintStyles(
  el: Element & { style: CSSStyleDeclaration },
  view: Window,
  convertColor: (cssColor: string) => string | null,
): void {
  const computed = view.getComputedStyle(el);
  const { style } = el;

  for (const prop of DIRECT_COLOR_STYLE_PROPS) {
    const raw = readStyleColorCandidate(el, computed, style, prop);
    if (!cssValueNeedsSanitize(raw)) {
      continue;
    }
    writeSanitizedStyle(style, prop, convertColor(raw) ?? fallbackDirectColor(prop));
  }

  for (const prop of COMPOSITE_COLOR_STYLE_PROPS) {
    const raw = readStyleColorCandidate(el, computed, style, prop);
    if (!cssValueNeedsSanitize(raw)) {
      continue;
    }
    const fallbackToken = prop === 'backgroundImage' ? '#ffffff' : 'transparent';
    const rewritten = rewriteUnsupportedCssColors(
      raw,
      (token) => convertColor(token) ?? fallbackToken,
    );
    if (rewritten !== raw) {
      writeSanitizedStyle(style, prop, rewritten);
    }
  }
}

function resolveSanitizeView(clonedRoot: Document): Window | null {
  if (clonedRoot.defaultView != null) {
    return clonedRoot.defaultView;
  }
  // Same-document fallback only (live export never uses orphan HTMLDocuments).
  if (typeof window !== 'undefined' && clonedRoot === window.document) {
    return window;
  }
  return null;
}

function walkAndSanitize(
  root: Element,
  view: Window,
  convertColor: (cssColor: string) => string | null,
): void {
  if (isPaintElement(root)) {
    sanitizeElementPaintStyles(root, view, convertColor);
  }
  const descendants = root.querySelectorAll('*');
  descendants.forEach((node) => {
    if (isPaintElement(node)) {
      sanitizeElementPaintStyles(node, view, convertColor);
    }
  });
}

/**
 * Walk the cloned capture tree (including html2canvas pseudo materializations)
 * and inline rgb/hex paint styles so html2canvas does not throw
 * `Attempting to parse an unsupported color function "color"`.
 *
 * @throws {FullPageExportStageError} when the clone has no usable defaultView
 *   (sanitization cannot run — capture must not proceed silently).
 */
export function sanitizeClonedExportColors(
  clonedRoot: Document,
  captureRoot: HTMLElement | null,
): void {
  const view = resolveSanitizeView(clonedRoot);
  if (view == null) {
    throw new FullPageExportStageError(
      'capture',
      'Export color sanitization requires a document defaultView on the capture clone. Retry the export, or refresh the page.',
    );
  }

  const convertColor = createCssColorToRgbConverter(clonedRoot);
  const roots: Element[] = [];
  if (captureRoot != null && isPaintElement(captureRoot)) {
    roots.push(captureRoot);
  } else if (clonedRoot.body != null && isPaintElement(clonedRoot.body)) {
    roots.push(clonedRoot.body);
  }

  if (isPaintElement(clonedRoot.documentElement)) {
    sanitizeElementPaintStyles(clonedRoot.documentElement, view, convertColor);
  }
  if (
    clonedRoot.body != null &&
    isPaintElement(clonedRoot.body) &&
    !roots.includes(clonedRoot.body)
  ) {
    sanitizeElementPaintStyles(clonedRoot.body, view, convertColor);
  }

  for (const root of roots) {
    walkAndSanitize(root, view, convertColor);
  }

  // G5: html2canvas materializes ::before/::after as <html2canvaspseudoelement>.
  // Also catch orphans under body if captureRoot walk missed them.
  clonedRoot.querySelectorAll(HTML2CANVAS_PSEUDO_SELECTOR).forEach((node) => {
    if (isPaintElement(node)) {
      sanitizeElementPaintStyles(node, view, convertColor);
    }
  });
}
